// src/pages/healthcare/results/testOrderComponents/TestOrderDetails.tsx

import React, { useMemo } from "react";
import { Select, DatePicker, Row, Col, Divider, Switch, Typography, Tooltip } from "antd";
import { Segmented } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import SectionCard from "../../../../components/shared/SectionCard";
import StatusTag from "../../../../components/shared/StatusTag";
import dayjs from "dayjs";

import TestBasicInfo from "./TestBasicInfo";

import { Test, CustomTestPackage, TestCategory, TestType, PRIORITY_OPTIONS, STATUS_OPTIONS } from "../../../../types/healthcare/TestTypes";
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";
import { BloodTestHint } from "../CreateTestOrderModal";

const { Text } = Typography;

interface TestOrderDetailsProps {
	currentOrder: Partial<Test>;
	customPackages: CustomTestPackage[];
	orderMode: "package" | "direct";
	onOrderModeChange: (mode: "package" | "direct") => void;
	availableTestCategories: TestCategory[];
	availableTestTypes: TestType[];
	bloodTestHints?: Record<string, BloodTestHint>;
	onDirectCategoryChange: (categoryValue: string) => void;
	isNotLocked: boolean;
	isLoading: boolean;
	handleFieldChange: (field: string, value: any) => void;
	handlePackageChange: (packageIds: number[]) => void;
}

const TestOrderDetails: React.FC<TestOrderDetailsProps> = ({
	currentOrder,
	customPackages,
	orderMode,
	onOrderModeChange,
	availableTestCategories,
	availableTestTypes,
	bloodTestHints,
	onDirectCategoryChange,
	isNotLocked,
	isLoading,
	handleFieldChange,
	handlePackageChange,
}) => {
	const dateFormat = loadUserPreferences().dateFormat.toUpperCase();
	const isEditing = Boolean(currentOrder.id);

	// An existing order is a "direct" order when it carries a category/type but no package.
	// Such orders must render the category/type controls (read-only when locked) instead of
	// the empty package selector — see bug 86d3gy359.
	const hasPackages =
		(currentOrder.custom_test_packages?.length ?? 0) > 0 ||
		(Array.isArray(currentOrder.custom_test_package_ids) && currentOrder.custom_test_package_ids.length > 0);
	const isDirectOrder =
		isEditing && !hasPackages && (Boolean(currentOrder.test_category) || (currentOrder.test_type?.length ?? 0) > 0);

	const packageOptions = useMemo(
		() =>
			customPackages
				.map((pkg) => ({
					label: pkg.name,
					value: pkg.id,
					description: pkg.description,
					analysisTypes: pkg.analysis_type_details?.map((t) => t.name).join(", "),
				}))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[customPackages],
	);

	const selectedPackage = useMemo(() => {
		return customPackages.find(
			(pkg) =>
				pkg.id ===
				(Array.isArray(currentOrder?.custom_test_package_ids) ? currentOrder.custom_test_package_ids[0] : currentOrder?.custom_test_packages?.[0]?.id),
		);
	}, [customPackages, currentOrder?.custom_test_package_ids]);

	const selectedPackageIds = useMemo(() => {
		if (Array.isArray(currentOrder?.custom_test_package_ids)) {
			return currentOrder.custom_test_package_ids;
		} else if (Array.isArray(currentOrder.custom_test_packages)) {
			return currentOrder.custom_test_packages.map((pkg) => pkg.id);
		}
		return [];
	}, [currentOrder?.custom_test_package_ids, currentOrder?.custom_test_packages]);

	const categoryOptions = useMemo(
		() => availableTestCategories.map((cat) => ({ label: cat.name, value: cat.value })),
		[availableTestCategories],
	);

	// Annotate each test-type option with its clinical hint (resolved from the seeded
	// blood-test structure by value/alias). Purely additive — selection still uses
	// label/value exactly as before; the hint is only shown in the option render.
	const testTypeOptions = useMemo(
		() =>
			availableTestTypes.map((t) => ({
				label: t.name,
				value: t.value,
				hint: bloodTestHints?.[t.value?.toLowerCase()] || bloodTestHints?.[t.name?.toLowerCase()],
			})),
		[availableTestTypes, bloodTestHints],
	);

	return (
		<Row gutter={[12, 12]} style={{ height: "100%" }}>
			<Col xs={24} lg={10}>
				<SectionCard title="Test Selection">
					<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

						{/* Mode toggle — hidden when editing an existing order */}
						{!isEditing && (
							<div>
								<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 6 }}>Ordering Mode</Text>
								<Segmented
									options={[
										{ label: "Test Package", value: "package" },
										{ label: "Category & Type", value: "direct" },
									]}
									value={orderMode}
									onChange={(val) => onOrderModeChange(val as "package" | "direct")}
									disabled={!isNotLocked}
									block
								/>
							</div>
						)}

						{/* ── PACKAGE MODE ─────────────────────────────────────── */}
						{((isEditing && !isDirectOrder) || (!isEditing && orderMode === "package")) && (
							<>
								<div>
									<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 6 }}>
										Test packages <span style={{ color: "var(--mg-danger)" }}>*</span>
									</Text>
									<Select
										mode="multiple"
										size="small"
										value={selectedPackageIds}
										options={packageOptions}
										onChange={(values) => handlePackageChange(values)}
										placeholder="Select test packages"
										style={{ width: "100%" }}
										showSearch
										filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
										disabled={!isNotLocked || isLoading}
										loading={isLoading}
										maxTagCount="responsive"
										optionRender={(option) => (
											<div>
												<div style={{ fontWeight: 600, fontSize: 12 }}>{option.label}</div>
												{option.data.description && (
													<div style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>{option.data.description}</div>
												)}
												{option.data.analysisTypes && (
													<div style={{ fontSize: 11, marginTop: 2, color: "var(--mg-text-secondary)" }}>
														Includes: {option.data.analysisTypes}
													</div>
												)}
											</div>
										)}
									/>
								</div>

								{selectedPackage && (
									<div
										style={{
											padding: "8px 12px",
											borderRadius: 6,
											background: "var(--mg-bg-subtle)",
											border: "1px solid var(--mg-border)",
											fontSize: 12,
										}}
									>
										<div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxHeight: 96, overflowY: "auto", marginBottom: selectedPackage.requires_fasting || selectedPackage.requires_contrast ? 6 : 0 }}>
											{selectedPackage.analysis_type_details?.map((type) => (
												<StatusTag key={type.value} status="info" label={type.name} size="small" />
											))}
										</div>
										{selectedPackage.requires_fasting && (
											<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
												<InfoCircleOutlined style={{ color: "var(--mg-danger)", fontSize: 11 }} />
												<Text type="danger" style={{ fontSize: 11 }}>Fasting required</Text>
											</div>
										)}
										{selectedPackage.requires_contrast && (
											<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
												<InfoCircleOutlined style={{ color: "var(--mg-danger)", fontSize: 11 }} />
												<Text type="danger" style={{ fontSize: 11 }}>Contrast required</Text>
											</div>
										)}
									</div>
								)}
							</>
						)}

						{/* ── DIRECT MODE ──────────────────────────────────────── */}
						{(isDirectOrder || (!isEditing && orderMode === "direct")) && (
							<>
								<div>
									<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 6 }}>
										Test category <span style={{ color: "var(--mg-danger)" }}>*</span>
									</Text>
									<Select
										size="small"
										value={currentOrder?.test_category || undefined}
										options={categoryOptions}
										onChange={(value) => onDirectCategoryChange(value)}
										placeholder="Select test category"
										style={{ width: "100%" }}
										showSearch
										filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
										disabled={!isNotLocked || isLoading}
										loading={isLoading}
									/>
								</div>

								<div>
									<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 6 }}>
										Test types <span style={{ color: "var(--mg-danger)" }}>*</span>
									</Text>
									<Select
										mode="multiple"
										size="small"
										value={currentOrder?.test_type || []}
										options={testTypeOptions}
										onChange={(values) => handleFieldChange("test_type", values)}
										placeholder={currentOrder?.test_category ? "Select test types" : "Select a category first"}
										style={{ width: "100%" }}
										showSearch
										filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
										disabled={!isNotLocked || !currentOrder?.test_category || testTypeOptions.length === 0}
										maxTagCount="responsive"
										listHeight={320}
										optionRender={(option) => {
											const hint = option.data.hint as BloodTestHint | undefined;
											const detail = hint?.clinicalUse || hint?.description;
											return (
												<div>
													<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
														<span style={{ fontSize: 12, fontWeight: 600 }}>{option.label}</span>
														{hint?.units && (
															<Text type="secondary" style={{ fontSize: 10 }}>({hint.units})</Text>
														)}
														{detail && (
															<Tooltip title={detail}>
																<InfoCircleOutlined style={{ fontSize: 11, color: "var(--mg-text-secondary)" }} />
															</Tooltip>
														)}
													</div>
													{detail && (
														<div style={{ fontSize: 11, color: "var(--mg-text-secondary)", whiteSpace: "normal" }}>{detail}</div>
													)}
												</div>
											);
										}}
									/>
								</div>

								<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0" }}>
									<div>
										<Text style={{ fontSize: 12, fontWeight: 500 }}>Send to company lab</Text>
										<br />
										<Text type="secondary" style={{ fontSize: 11 }}>Create a record in your company's LabIQ system</Text>
									</div>
									<Switch
										checked={currentOrder?.send_to_lab ?? false}
										onChange={(checked) => handleFieldChange("send_to_lab", checked)}
										disabled={!isNotLocked}
									/>
								</div>
							</>
						)}

						<Divider style={{ margin: "2px 0" }} />

						<Row gutter={[8, 8]}>
							<Col xs={24} md={8}>
								<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 4 }}>Priority</Text>
								<Select
									size="small"
									value={currentOrder?.priority || "routine"}
									options={PRIORITY_OPTIONS}
									onChange={(value) => handleFieldChange("priority", value)}
									style={{ width: "100%" }}
									disabled={!isNotLocked}
								/>
							</Col>

							<Col xs={24} md={8}>
								<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 4 }}>Status</Text>
								<Select
									size="small"
									value={currentOrder?.status || "ordered"}
									options={STATUS_OPTIONS}
									onChange={(value) => handleFieldChange("status", value)}
									style={{ width: "100%" }}
									disabled={!isNotLocked}
								/>
							</Col>

							<Col xs={24} md={8}>
								<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 4 }}>Test date</Text>
								<DatePicker
									size="small"
									value={currentOrder?.test_date ? dayjs(currentOrder.test_date) : null}
									onChange={(date) => handleFieldChange("test_date", date?.toISOString())}
									format={dateFormat}
									style={{ width: "100%" }}
									disabled={!isNotLocked}
								/>
							</Col>
						</Row>
					</div>
				</SectionCard>
			</Col>

			<Col xs={24} lg={14}>
				<TestBasicInfo currentOrder={currentOrder} selectedPackage={selectedPackage} isNotLocked={isNotLocked} handleFieldChange={handleFieldChange} />
			</Col>
		</Row>
	);
};

export default TestOrderDetails;
